import axios from "axios";
import { useAuthStore } from "@/stores";

// Construct API base URL safely
const serverUrl = (process.env.VUE_APP_SERVER_URL || "").replace(/\/$/, "");
const serverPort = process.env.VUE_APP_SERVER_PORT;
axios.defaults.baseURL = `${serverUrl}${
  serverPort ? ":" + serverPort : ""
}/api/`;

// SECURITY: Enable sending cookies with cross-origin requests
// Required for httpOnly refresh token cookie
const axiosInstance = axios.create({
  withCredentials: true,
});

// Queue for requests waiting for token refresh
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

axiosInstance.interceptors.request.use(
  async (config) => {
    const authStore = useAuthStore();

    // Check if store is already refreshing
    if (authStore.isRefreshing) {
      // Wait for the refresh to complete
      while (authStore.isRefreshing) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    } else {
      // Only check authorization if we're not already refreshing
      const accessTokenExpiresAt = authStore.accessTokenExpiresAt;
      // SECURITY: accessToken is memory-only, so check if it exists OR is expired
      const needsRefresh =
        !authStore.accessToken || Date.now() >= accessTokenExpiresAt;

      // hasSession indicates we believe a valid httpOnly refresh cookie exists
      if (needsRefresh && authStore.hasSession && !authStore.isRefreshing) {
        try {
          await authStore.refreshAccessToken();
        } catch (error) {
          console.error("Failed to refresh token in interceptor:", error);
          return Promise.reject(error);
        }
      }
    }

    const accessToken = authStore.accessToken;
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle 401 errors
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const authStore = useAuthStore();
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (authStore.isRefreshing) {
        // If we're already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;

      try {
        const newToken = await authStore.refreshAccessToken();
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        // The store will handle logout/redirect in refreshAccessToken
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
